// Recurring Invoice dashboard. Every figure is worked out from the profile and
// invoice lists the page already loaded — nothing here is invented.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Btn, IC, Seg } from "./RecurringParts";
import {
  DAYS,
  INVOICE_TYPES,
  TYPE_COL,
  addDays,
  compact,
  counters,
  dayMonth,
  fmtDLong,
  fmtPct,
  iso,
  monthLbl,
  monthSeries,
  monthlyValue,
  money,
  money2,
  pct,
  r2,
  sumNet,
  today,
  daysTo,
  monthKey,
} from "./recurringModel";

const useWidth = (fallback) => {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setW(el.clientWidth || fallback);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [fallback]);
  return [ref, w];
};

const niceTicks = (max, n) => {
  const raw = max / n;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const out = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(r2(v));
  if (out[out.length - 1] < max) out.push(r2(out[out.length - 1] + step));
  return out;
};
const axisFmt = (v, top) => {
  if (top >= 1000000) return v === 0 ? "0" : r2(v / 1000000) + "M";
  if (top >= 10000) return v === 0 ? "0" : Math.round(v / 1000) + "K";
  return money(v);
};
const roundTop = (x, y, w, h, rad) => {
  if (h <= 0) return "";
  const r = Math.min(rad, w / 2, h);
  return (
    "M" + r2(x) + " " + r2(y + h) + "V" + r2(y + r) + "a" + r + " " + r + " 0 0 1 " + r + " " + -r +
    "h" + r2(w - 2 * r) + "a" + r + " " + r + " 0 0 1 " + r + " " + r + "V" + r2(y + h) + "Z"
  );
};

const Delta = ({ cur, prev }) => {
  if (!prev) return <span className="axrc-delta flat">new</span>;
  const p = ((cur - prev) / Math.abs(prev)) * 100;
  const flat = Math.abs(p) < 0.5;
  return (
    <span className={"axrc-delta " + (flat ? "flat" : p > 0 ? "up" : "down")}>
      {!flat && (
        <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d={p > 0 ? "M6 9.5V2.5M3 5.5 6 2.5l3 3" : "M6 2.5v7M3 6.5 6 9.5l3-3"} />
        </svg>
      )}
      {fmtPct(p)}
    </span>
  );
};

const Spark = ({ vals, w = 240, h = 40 }) => {
  if (vals.length < 2) return <svg className="axrc-spark" width={w} height={h} aria-hidden="true" />;
  let mn = Math.min(...vals);
  let mx = Math.max(...vals);
  const pad = (mx - mn) * 0.16 || 1;
  mn -= pad;
  mx += pad;
  const step = w / (vals.length - 1);
  const pts = vals.map((v, i) => [r2(i * step), r2(h - ((v - mn) / (mx - mn)) * h)]);
  const d = pts.map((p, i) => (i ? "L" : "M") + p[0] + " " + p[1]).join(" ");
  const a = pts[pts.length - 2];
  const b = pts[pts.length - 1];
  return (
    <svg className="axrc-spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
      <path d={d} fill="none" stroke="var(--ghost)" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <path d={`M${a[0]} ${a[1]}L${b[0]} ${b[1]}`} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <circle cx={b[0]} cy={b[1]} r="2.6" fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" />
    </svg>
  );
};

/* ---------------------------------------------------------------- trend */

const TrendChart = ({ ser }) => {
  const [ref, W] = useWidth(700);
  const [hot, setHot] = useState(null);
  const H = 250;
  const padL = 54;
  const padR = 12;
  const padT = 12;
  const padB = 24;
  const iw = Math.max(80, W - padL - padR);
  const ih = H - padT - padB;
  let mx = 1;
  ser.forEach((s) => {
    mx = Math.max(mx, s.invoiced, s.collected);
  });
  const ticks = niceTicks(mx, 4);
  const top = ticks[ticks.length - 1];
  const band = iw / ser.length;
  const bw = Math.min(24, band * 0.5);
  const X = (i) => padL + band * i + band / 2;
  const Y = (v) => padT + ih - (v / top) * ih;
  const every = Math.ceil(ser.length / Math.max(3, Math.floor(iw / 62)));
  let lastX = -1e9;
  const s = hot !== null ? ser[hot] : null;
  let tipLeft = 0;
  if (s) {
    tipLeft = X(hot) + 14;
    if (tipLeft + 180 > W) tipLeft = X(hot) - 194;
  }
  return (
    <div className="axrc-plot" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label="Invoiced and collected by month">
        {ticks.map((v) => (
          <g key={v}>
            <line className="gridline" x1={padL} x2={padL + iw} y1={r2(Y(v))} y2={r2(Y(v))} />
            <text className="axtext" x={padL - 7} y={r2(Y(v) + 3.5)} textAnchor="end">
              {axisFmt(v, top)}
            </text>
          </g>
        ))}
        {ser.map((x, i) => (
          <path key={x.k} className={"bar" + (hot === i ? " hot" : "")} d={roundTop(X(i) - bw / 2, Y(x.invoiced), bw, (x.invoiced / top) * ih, 4)} />
        ))}
        <path className="cmpline" d={ser.map((x, i) => (i ? "L" : "M") + r2(X(i)) + " " + r2(Y(x.collected))).join(" ")} />
        {ser.map((x, i) => (
          <circle key={x.k} cx={r2(X(i))} cy={r2(Y(x.collected))} r="4" fill="var(--s3)" stroke="var(--surface)" strokeWidth="2" />
        ))}
        {ser.map((x, i) => {
          const forced = i === ser.length - 1;
          if (i % every !== 0 && !forced) return null;
          if (forced && X(i) - lastX < 46) return null;
          lastX = X(i);
          return (
            <text key={x.k} className="axtext" x={r2(X(i))} y={H - 6} textAnchor="middle">
              {x.lbl}
            </text>
          );
        })}
        {s ? <line className="crosshair" x1={r2(X(hot))} x2={r2(X(hot))} y1={padT} y2={padT + ih} /> : null}
        {ser.map((x, i) => (
          <rect
            key={x.k}
            className="hit"
            x={r2(padL + band * i)}
            y={padT}
            width={r2(band)}
            height={ih}
            onPointerEnter={() => setHot(i)}
            onPointerMove={() => setHot(i)}
            onPointerLeave={() => setHot(null)}
          />
        ))}
      </svg>
      {s ? (
        <div className="axrc-tip" style={{ left: Math.max(2, tipLeft), top: Math.max(2, Y(Math.max(s.invoiced, s.collected)) - 12) }}>
          <div className="tt">{s.lbl}</div>
          <div className="tr">
            <span className="key" style={{ background: "var(--accent)" }} />
            <span className="tn">Invoiced</span>
            <span className="tv">{money(s.invoiced)}</span>
          </div>
          <div className="tr">
            <span className="key" style={{ background: "var(--s3)" }} />
            <span className="tn">Collected</span>
            <span className="tv">{money(s.collected)}</span>
          </div>
          <div className="tr">
            <span className="key" />
            <span className="tn">Invoices</span>
            <span className="tv">{s.count}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/* ---------------------------------------------------------------- next 14 days */

const UpcomingChart = ({ days }) => {
  const [ref, W] = useWidth(320);
  const [hot, setHot] = useState(null);
  const H = 168;
  const padL = 40;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  const iw = Math.max(60, W - padL - padR);
  const ih = H - padT - padB;
  const mx = Math.max(1, ...days.map((d) => d.n));
  const ticks = niceTicks(mx, 3);
  const top = ticks[ticks.length - 1];
  const band = iw / days.length;
  const bw = Math.min(16, band * 0.58);
  const X = (i) => padL + band * i + band / 2;
  const Y = (v) => padT + ih - (v / top) * ih;
  let lastLbl = -1e9;
  const d = hot !== null ? days[hot] : null;
  let left = 0;
  if (d) {
    left = X(hot) + 12;
    if (left + 170 > W) left = X(hot) - 182;
  }
  return (
    <div className="axrc-plot" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label="Profiles due in the next 14 days">
        {ticks.map((v) => (
          <g key={v}>
            <line className="gridline" x1={padL} x2={padL + iw} y1={r2(Y(v))} y2={r2(Y(v))} />
            <text className="axtext" x={padL - 6} y={r2(Y(v) + 3.5)} textAnchor="end">
              {Math.round(v)}
            </text>
          </g>
        ))}
        {days.map((x, i) => (
          <path key={x.k} className={"bar" + (hot === i ? " hot" : "")} d={roundTop(X(i) - bw / 2, Y(x.n), bw, (x.n / top) * ih, 3)} />
        ))}
        {days.map((x, i) => {
          const forced = i === days.length - 1;
          if (i % 3 !== 0 && !forced) return null;
          if (X(i) - lastLbl < 42) return null;
          lastLbl = X(i);
          return (
            <text key={x.k} className="axtext" x={r2(X(i))} y={H - 5} textAnchor="middle">
              {x.lbl}
            </text>
          );
        })}
        {days.map((x, i) => (
          <rect
            key={x.k}
            className="hit"
            x={r2(padL + band * i)}
            y={padT}
            width={r2(band)}
            height={ih}
            onPointerEnter={() => setHot(i)}
            onPointerMove={() => setHot(i)}
            onPointerLeave={() => setHot(null)}
          />
        ))}
      </svg>
      {d ? (
        <div className="axrc-tip" style={{ left: Math.max(2, left), top: Math.max(2, Y(d.n) - 10) }}>
          <div className="tt">
            {d.lbl}
            {hot === 0 ? " · today" : ""}
          </div>
          <div className="tr">
            <span className="key" style={{ background: "var(--accent)" }} />
            <span className="tn">Profiles due</span>
            <span className="tv">{d.n}</span>
          </div>
          <div className="tr">
            <span className="key" />
            <span className="tn">Value</span>
            <span className="tv">{money(d.v)}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const SEVNAME = { crit: "Urgent", warnx: "Soon", info: "Watch" };

/* ---------------------------------------------------------------- the screen */

const RecurringDashboard = ({ profiles, invoices, cur, branchName, onTile, onQueue, onCustomer, onRunNow, onNew, permit, running }) => {
  const [range, setRange] = useState(12);
  const [trendTable, setTrendTable] = useState(false);
  const [mixTable, setMixTable] = useState(false);

  const c = useMemo(() => counters(profiles, invoices), [profiles, invoices]);
  const active = useMemo(() => profiles.filter((p) => p.status === "Active"), [profiles]);
  const mrr = useMemo(() => active.reduce((a, p) => a + monthlyValue(p), 0), [active]);
  const ser14 = useMemo(() => monthSeries(invoices, 14), [invoices]);
  const ser = useMemo(() => monthSeries(invoices, range), [invoices, range]);
  const thisM = ser14[ser14.length - 1];
  const lastFull = ser14[ser14.length - 2];
  const prevFull = ser14[ser14.length - 3];
  const pending = invoices.filter((x) => x.pay === "Pending");
  const pendingAmt = sumNet(pending);

  const tiles = [
    { k: "dueToday", l: "Due today", v: c.dueToday, s: "raised by today's run", cls: c.dueToday ? "acc" : "" },
    { k: "dueWeek", l: "Due this week", v: c.dueWeek, s: "next seven days" },
    { k: "genMonth", l: "Generated this month", v: c.genMonth, s: cur + " " + compact(thisM.invoiced) + " billed", cls: "good" },
    { k: "failed", l: "Failed", v: c.failed, s: c.failed ? "needs a human" : "all clean", cls: c.failed ? "bad" : "good" },
    { k: "expiring", l: "Expiring this month", v: c.expiring, s: "renew or let them lapse", cls: "warn" },
    {
      k: "paid",
      l: "Collected this month",
      v: cur + " " + compact(thisM.collected),
      s: fmtPct(pct(thisM.collected, thisM.invoiced), 0) + " of what was billed",
      cls: "good",
    },
    { k: "pending", l: "Outstanding", v: cur + " " + compact(pendingAmt), s: "across all cycles", cls: "warn" },
    { k: "cancelled", l: "Cancelled", v: c.cancelled, s: "no longer billed" },
  ];

  const mix = useMemo(() => {
    const rows = INVOICE_TYPES.map((t) => {
      const list = active.filter((p) => p.type === t);
      return { n: t, c: TYPE_COL[t], v: r2(list.reduce((a, p) => a + monthlyValue(p), 0)), cnt: list.length };
    }).sort((a, b) => b.v - a.v);
    return { rows, total: rows.reduce((a, x) => a + x.v, 0) };
  }, [active]);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 14; i++) {
      const d = addDays(today(), i);
      const k = iso(d);
      const list = active.filter((p) => p.next === k);
      out.push({ k, lbl: dayMonth(d), n: list.length, v: r2(list.reduce((a, p) => a + p.net, 0)) });
    }
    return out;
  }, [active]);

  const queue = useMemo(() => {
    const mk = monthKey(iso(today()));
    const overdue = pending.filter((x) => daysTo(x.due) < 0);
    const noEmail = active.filter((p) => p.autoEmail && !p.email);
    const paused = profiles.filter((p) => p.status === "Paused");
    const expiring = profiles.filter((p) => p.end && monthKey(p.end) === mk && p.status !== "Cancelled");
    const out = [];
    if (c.failed)
      out.push({ sev: "crit", ic: IC.warn, k: "failed", t: c.failed + " profile" + (c.failed === 1 ? "" : "s") + " failed to generate", s: "the scheduler could not raise the invoice", n: c.failed + " profiles" });
    if (overdue.length)
      out.push({ sev: "crit", ic: IC.clock, k: "overdue", t: "Overdue recurring invoices", s: "past the payment terms", n: cur + " " + compact(sumNet(overdue)) });
    if (c.dueToday) out.push({ sev: "info", ic: IC.cal, k: "dueToday", t: c.dueToday + " profiles are due today", s: "today's run will raise them", n: c.dueToday + " profiles" });
    if (expiring.length) out.push({ sev: "warnx", ic: IC.cal, k: "expiring", t: expiring.length + " contracts end this month", s: "renew before the last invoice", n: expiring.length + " profiles" });
    if (paused.length)
      out.push({
        sev: "warnx",
        ic: IC.pause,
        k: "paused",
        t: paused.length + " profiles are paused",
        s: "nothing is being billed on them",
        n: cur + " " + compact(paused.reduce((a, p) => a + monthlyValue(p), 0)) + "/mo",
      });
    if (noEmail.length) out.push({ sev: "warnx", ic: IC.mail, k: "noemail", t: "Auto email on, but no address", s: "the invoice will not reach anyone", n: noEmail.length + " profiles" });
    out.push({ sev: "info", ic: IC.cash, k: "pending", t: "Pending collection", s: "across every cycle raised", n: cur + " " + compact(pendingAmt) });
    return out;
  }, [c, pending, active, profiles, cur, pendingAmt]);

  const topCust = useMemo(() => {
    const by = {};
    active.forEach((p) => {
      by[p.custGuid] = by[p.custGuid] || { guid: p.custGuid, name: p.custName, v: 0 };
      by[p.custGuid].v += monthlyValue(p);
    });
    return Object.values(by)
      .sort((a, b) => b.v - a.v)
      .slice(0, 7);
  }, [active]);
  const topMax = topCust.length ? topCust[0].v : 1;

  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>Recurring Invoice dashboard</h1>
          <div className="sub">
            {DAYS[today().getDay()]}, {fmtDLong(iso(today()))} · {c.active} live profiles{branchName ? " · " + branchName : ""}
          </div>
        </div>
        <div className="sp" />
        <Seg
          options={[
            { v: 3, l: "3 months" },
            { v: 6, l: "6 months" },
            { v: 12, l: "12 months" },
          ]}
          value={range}
          onChange={setRange}
        />
        {permit.add && (
          <Btn icon={IC.play} onClick={onRunNow} disabled={running}>
            {running ? "Running…" : "Run scheduler now"}
          </Btn>
        )}
        {permit.add && (
          <Btn kind="primary" icon={IC.plus} onClick={onNew}>
            New profile
          </Btn>
        )}
      </div>

      <div className="axrc-dashbody">
        <section className="axrc-kpirow">
          <div className="axrc-card axrc-hero">
            <div>
              <div className="lbl">Monthly recurring revenue</div>
              <div className="fig">
                <span className="cur">{cur}</span>
                {money(mrr)}
              </div>
              <div className="row">
                <Delta cur={lastFull.invoiced} prev={prevFull.invoiced} />
                <span className="axrc-dim">
                  {monthLbl(lastFull.k)} vs {monthLbl(prevFull.k)} invoiced
                </span>
              </div>
            </div>
            <Spark vals={ser14.slice(0, -1).slice(-12).map((x) => x.invoiced)} />
            <div className="foot">
              <span>
                Annual run rate <b>{cur + " " + compact(mrr * 12)}</b>
              </span>
              <span>
                <b>{c.active}</b> profiles contribute
              </span>
            </div>
          </div>
          <div className="axrc-tiles">
            {tiles.map((t) => (
              <button key={t.k} type="button" className={"axrc-tile " + (t.cls || "")} onClick={() => onTile(t.k)}>
                <span className="lbl">{t.l}</span>
                <span className="val">{t.v}</span>
                <span className="sub">{t.s}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="axrc-g12">
          <div className="axrc-card c8">
            <div className="axrc-chead">
              <h2>Invoiced and collected</h2>
              <span className="note">
                last {range} months · {ser.reduce((a, x) => a + x.count, 0)} invoices
              </span>
              <span className="sp" />
              <button type="button" className={"axrc-minibtn" + (trendTable ? " on" : "")} onClick={() => setTrendTable((v) => !v)}>
                {trendTable ? "Chart" : "Table"}
              </button>
            </div>
            <div className="axrc-cbody">
              {trendTable ? (
                <div className="axrc-tview">
                  <table>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="num">Invoices</th>
                        <th className="num">Invoiced</th>
                        <th className="num">Collected</th>
                        <th className="num">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ser.map((x) => (
                        <tr key={x.k}>
                          <td>{x.lbl}</td>
                          <td className="num">{x.count}</td>
                          <td className="num">{money2(x.invoiced)}</td>
                          <td className="num">{money2(x.collected)}</td>
                          <td className="num warn">{money2(x.invoiced - x.collected)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <TrendChart ser={ser} />
              )}
              <div className="axrc-legend">
                <span className="li">
                  <span className="sw" style={{ background: "var(--accent)" }} />
                  Invoiced
                </span>
                <span className="li">
                  <span className="ln" style={{ background: "var(--s3)" }} />
                  Collected
                </span>
                <span className="li axrc-dim">hover for the exact figure</span>
              </div>
            </div>
          </div>

          <div className="axrc-card c4">
            <div className="axrc-chead">
              <h2>Needs attention</h2>
              <span className="sp" />
              <span className="note">
                {queue.filter((x) => x.sev === "crit").length} urgent · {queue.length} open
              </span>
            </div>
            <div className="axrc-cbody">
              <div className="axrc-queue">
                {queue.map((x) => (
                  <button key={x.k} type="button" className={"axrc-qrow " + x.sev} onClick={() => onQueue(x.k)}>
                    <span className="ic">{x.ic}</span>
                    <span className="txt">
                      <span className="t">{x.t}</span>
                      <span className="s">
                        <span className={"axrc-sevlbl " + x.sev}>{SEVNAME[x.sev]}</span>
                        <em>{x.s}</em>
                      </span>
                    </span>
                    <span className="n">{x.n}</span>
                    <span className="ch">{IC.chev}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="axrc-card c4">
            <div className="axrc-chead">
              <h2>Recurring revenue by type</h2>
              <span className="sp" />
              <button type="button" className={"axrc-minibtn" + (mixTable ? " on" : "")} onClick={() => setMixTable((v) => !v)}>
                {mixTable ? "Chart" : "Table"}
              </button>
            </div>
            <div className="axrc-cbody">
              {mixTable ? (
                <div className="axrc-tview">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice type</th>
                        <th className="num">Profiles</th>
                        <th className="num">Per month</th>
                        <th className="num">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mix.rows.map((x) => (
                        <tr key={x.n}>
                          <td>{x.n}</td>
                          <td className="num">{x.cnt}</td>
                          <td className="num">{money2(x.v)}</td>
                          <td className="num">{fmtPct(pct(x.v, mix.total))}</td>
                        </tr>
                      ))}
                      <tr>
                        <td>
                          <b>Total</b>
                        </td>
                        <td className="num">
                          <b>{mix.rows.reduce((a, x) => a + x.cnt, 0)}</b>
                        </td>
                        <td className="num">
                          <b>{money2(mix.total)}</b>
                        </td>
                        <td className="num">{mix.total ? "100.0%" : "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <>
                  <div className="axrc-stack">
                    {mix.rows.map((x) => (
                      <i key={x.n} style={{ width: pct(x.v, mix.total).toFixed(2) + "%", background: x.c }} />
                    ))}
                  </div>
                  <div className="axrc-stkey">
                    {mix.rows.map((x) => (
                      <span key={x.n} className="k">
                        <span className="sw" style={{ background: x.c }} />
                        <span className="kn">{x.n}</span>
                        <span className="kv">{money(x.v)}</span>
                        <span className="kp">{fmtPct(pct(x.v, mix.total), 0)}</span>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="axrc-card c4">
            <div className="axrc-chead">
              <h2>Due in the next 14 days</h2>
              <span className="sp" />
              <span className="note">
                {days.reduce((a, d) => a + d.n, 0)} profiles · {cur} {compact(days.reduce((a, d) => a + d.v, 0))}
              </span>
            </div>
            <div className="axrc-cbody">
              <UpcomingChart days={days} />
            </div>
          </div>

          <div className="axrc-card c4">
            <div className="axrc-chead">
              <h2>Top customers by recurring value</h2>
              <span className="sp" />
              <span className="note">per month</span>
            </div>
            <div className="axrc-cbody">
              <div className="axrc-blist">
                {topCust.length ? (
                  topCust.map((x) => (
                    <button key={x.guid} type="button" className="axrc-brow" onClick={() => onCustomer(x.guid)}>
                      <span>
                        <span className="nm">{x.name}</span>
                        <span className="track">
                          <i style={{ width: pct(x.v, topMax).toFixed(1) + "%" }} />
                        </span>
                      </span>
                      <span className="v">
                        {money(x.v)}
                        <small>per month</small>
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="axrc-nores">No active profile yet.</div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};

export default RecurringDashboard;
